import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (!storedToken) {
        setLoading(false);
        return;
      }

      // Timeout: if /auth/me takes > 8s, stop waiting
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);

      try {
        const response = await api.get('/auth/me', { signal: controller.signal });
        setUser(response.data);

        // Silent token renewal: decode token to check expiry
        try {
          const payload = JSON.parse(atob(storedToken.split('.')[1]));
          const expiresAt = payload.exp * 1000;
          const hoursLeft = (expiresAt - Date.now()) / (1000 * 60 * 60);
          // If less than 48h remaining, request a fresh token
          if (hoursLeft < 48 && hoursLeft > 0) {
            api.post('/auth/refresh-token').then(res => {
              if (res.data?.access_token) {
                localStorage.setItem('token', res.data.access_token);
                setToken(res.data.access_token);
              }
            }).catch(() => {}); // Silent — don't break init if renewal fails
          }
        } catch {} // Token decode failed — not critical
      } catch (error) {
        if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
          console.warn('Auth init timed out — continuing without session');
        } else {
          console.error('Auth init error:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      } finally {
        clearTimeout(timeout);
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const register = async (name, email, password) => {
    const response = await api.post('/auth/register', { name, email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const completeOnboarding = async () => {
    await api.put('/auth/onboarding-complete');
    setUser(prev => ({ ...prev, onboarding_completed: true }));
  };

  const updateProfile = async (data) => {
    const body = typeof data === 'string' ? { name: data } : data;
    await api.put('/auth/profile', body);
    setUser(prev => ({ ...prev, ...body }));
  };

  const setAuthFromToken = (access_token, userData) => {
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    completeOnboarding,
    updateProfile,
    setAuthFromToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
